import { useCallback, useMemo, useState } from 'react';

import type { Ticket, TicketComplexitySurvey } from '@/model/Ticket/Ticket';
import { TICKET_COMPLEXITY_REASON } from '@/model/Ticket/TicketConstants';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

export interface TicketComplexitySurveyController {
  expectedTimeMinutes?: number;
  resolvedInTime: boolean | null;
  reasons: TICKET_COMPLEXITY_REASON[];
  otherText: string;
  actualTimeHours: number;
  actualTimeMinutes: number;
  submitting: boolean;
  isValid: boolean;
  setResolvedInTime: (value: boolean | null) => void;
  setOtherText: (value: string) => void;
  setActualTimeHours: (value: number | null) => void;
  setActualTimeMinutes: (value: number | null) => void;
  handleReasonChange: (reason: TICKET_COMPLEXITY_REASON, checked: boolean) => void;
  resetForm: () => void;
  submitSurvey: () => Promise<boolean>;
}

// Centraliza cuándo se debe pedir la encuesta para reutilizarlo en flujos encadenados.
export const shouldRequestTicketComplexitySurvey = (ticket?: Ticket) =>
  Boolean(ticket?.id && !ticket.has_complexity_survey);

// Expone el estado y envío de la encuesta sin atarlo a un Modal específico.
export const useTicketComplexitySurvey = (ticket?: Ticket): TicketComplexitySurveyController => {
  const { ticketRepository } = useRepositoriesContext();
  const [resolvedInTime, setResolvedInTimeState] = useState<boolean | null>(true);
  const [reasons, setReasons] = useState<TICKET_COMPLEXITY_REASON[]>([]);
  const [otherText, setOtherText] = useState('');
  const [actualTimeHours, setActualTimeHoursState] = useState(0);
  const [actualTimeMinutes, setActualTimeMinutesState] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const resetForm = useCallback(() => {
    setResolvedInTimeState(null);
    setReasons([]);
    setOtherText('');
    setActualTimeHoursState(0);
    setActualTimeMinutesState(0);
  }, []);
  const setResolvedInTime = useCallback((value: boolean | null) => {
    setResolvedInTimeState(value);
    // Si el ticket se resolvió en tiempo lógico no se deben conservar datos de complejidad previos.
    if (value === true) {
      setReasons([]);
      setOtherText('');
      setActualTimeHoursState(0);
      setActualTimeMinutesState(0);
    }
  }, []);
  const setActualTimeHours = useCallback((value: number | null) => {
    // Metadata: normaliza horas vacías a 0 y evita valores decimales antes del envío.
    setActualTimeHoursState(Math.max(0, Math.trunc(value ?? 0)));
  }, []);
  const setActualTimeMinutes = useCallback((value: number | null) => {
    // Metadata: los minutos son la parte mm de HH:mm, por eso se acotan entre 0 y 59.
    setActualTimeMinutesState(Math.min(59, Math.max(0, Math.trunc(value ?? 0))));
  }, []);
  const handleReasonChange = useCallback((reason: TICKET_COMPLEXITY_REASON, checked: boolean) => {
    if (checked) {
      setReasons(prev => [...prev, reason]);
    } else {
      setReasons(prev => prev.filter(r => r !== reason));
    }
  }, []);
  const isValid = useMemo(() => {
    const actualTotalMinutes = actualTimeHours * 60 + actualTimeMinutes;

    // Validación: si "No", requiere tiempo real, al menos 1 razón; si "Otros" requiere texto.
    if (resolvedInTime === null) return false;
    if (resolvedInTime === true) return true;
    if (actualTotalMinutes <= 0) return false;
    if (reasons.length === 0) return false;
    if (reasons.includes(TICKET_COMPLEXITY_REASON.OTHER) && !otherText.trim()) return false;

    return true;
  }, [actualTimeHours, actualTimeMinutes, otherText, reasons, resolvedInTime]);
  const submitSurvey = useCallback(async () => {
    if (!ticket?.id || !isValid) return false;

    const survey: TicketComplexitySurvey = {
      ticket: ticket.id,
      resolved_in_expected_time: resolvedInTime!,
    };

    // Solo enviar razones y tiempo real si la respuesta fue "No".
    if (resolvedInTime === false) {
      survey.dedicate_time = actualTimeHours * 60 + actualTimeMinutes;
      survey.reasons = reasons.join(','); // Metadata: el backend espera un string con IDs separados por coma.
      if (reasons.includes(TICKET_COMPLEXITY_REASON.OTHER)) {
        survey.other_text = otherText.trim();
      }
    } else {
      survey.dedicate_time = null;
    }

    setSubmitting(true);
    try {
      await ticketRepository.submitComplexitySurvey(survey);

      return true;
    } catch {
      // El error ya se muestra en TicketApi via message.error.
      return false;
    } finally {
      setSubmitting(false);
    }
  }, [
    actualTimeHours,
    actualTimeMinutes,
    isValid,
    otherText,
    reasons,
    resolvedInTime,
    ticket?.id,
    ticketRepository,
  ]);

  return {
    expectedTimeMinutes: ticket?.subcategory_expected_time,
    resolvedInTime,
    reasons,
    otherText,
    actualTimeHours,
    actualTimeMinutes,
    submitting,
    isValid,
    setResolvedInTime,
    setOtherText,
    setActualTimeHours,
    setActualTimeMinutes,
    handleReasonChange,
    resetForm,
    submitSurvey,
  };
};
